import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AvailabilityReportItem } from '@/types';

export interface AvailabilityFilters {
  quantityMin: number | null;
  quantityMax: number | null;
  counterparties: string[];
  caStatuses: string[];
  benchmarkFeeThreshold: number | null;
  excludeUpcomingCA: boolean;
  hideISINNone: boolean;
  excludeInternalColumns: boolean;
  accountForClientTransfers: boolean;
}

interface AvailabilityStore {
  filteredAvailabilityData: AvailabilityReportItem[];
  availabilityFilters: AvailabilityFilters;
  setFilteredAvailabilityData: (data: AvailabilityReportItem[]) => void;
  setAvailabilityFilters: (filters: AvailabilityFilters) => void;
  clearAvailabilityFilters: () => void;
  resetLocalFilter: (filterId: string) => void;
}

const defaultFilters: AvailabilityFilters = {
  quantityMin: null,
  quantityMax: null,
  counterparties: [],
  caStatuses: [],
  benchmarkFeeThreshold: 0,
  excludeUpcomingCA: true,
  hideISINNone: true,
  excludeInternalColumns: true,
  accountForClientTransfers: true,
};

export const useAvailabilityStore = create<AvailabilityStore>()(
  devtools(
    persist(
      (set) => ({
        filteredAvailabilityData: [],
        availabilityFilters: defaultFilters,
        
        setFilteredAvailabilityData: (data) => set({ filteredAvailabilityData: data }),
        
        setAvailabilityFilters: (filters) => set({ availabilityFilters: filters }),
        
        clearAvailabilityFilters: () => set({ availabilityFilters: defaultFilters }),

        // Every reset path must land on defaultFilters values (LR-598):
        // hand-written literals here let "reset" drift off-default —
        // accountForClientTransfers was reset to false while its default is
        // true, silently changing what the Quantity column means.
        resetLocalFilter: (filterId: string) => set((state) => {
          const f = { ...state.availabilityFilters };
          switch (filterId) {
            case 'quantity':
              f.quantityMin = defaultFilters.quantityMin;
              f.quantityMax = defaultFilters.quantityMax;
              break;
            case 'counterparties': f.counterparties = defaultFilters.counterparties; break;
            case 'caStatuses': f.caStatuses = defaultFilters.caStatuses; break;
            case 'benchmarkFeeThreshold': f.benchmarkFeeThreshold = defaultFilters.benchmarkFeeThreshold; break;
            case 'excludeUpcomingCA': f.excludeUpcomingCA = defaultFilters.excludeUpcomingCA; break;
            case 'hideISINNone': f.hideISINNone = defaultFilters.hideISINNone; break;
            case 'accountForClientTransfers':
              f.accountForClientTransfers = defaultFilters.accountForClientTransfers;
              break;
          }
          return { availabilityFilters: f };
        }),
      }),
      {
        name: 'availability-store',
        version: 0,
        partialize: (state) => ({ availabilityFilters: state.availabilityFilters }),
      }
    ),
    {
      name: 'availability-store', enabled: import.meta.env.DEV,
    }
  )
);

